<template>
  <div v-if="hasActions" class="panel-actions" :class="positionClasses">
    <template v-for="(item, index) in sortedPanelActions" :key="`action-${index}`">
      <!-- Single action -->
      <MAction
        v-if="item.type === 'single'"
        :data="item.action"
        :elementId="props.targetElementId"
        :registrable="true"
        class="panel-action-item"
      />

      <!-- Grouped actions with dropdown -->
      <div v-else-if="item.type === 'group'" class="action-group">
        <MAction
          :data="item.mainAction"
          :elementId="props.targetElementId"
          :in-group="true"
          :registrable="true"
          class="panel-action-item main-action"
        />
        <Button
          icon="pi pi-chevron-down"
          size="small"
          class="action-dropdown-toggle"
          @click="toggleDropdown($event, index)"
          aria-label="Показать дополнительные действия"
        />
        <Menu
          :ref="(el) => setMenuRef(el, index)"
          :model="getDropdownItems(item.actions)"
          :popup="true"
        />
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { usePageData } from '@/common/composables/usePageData'
import { useActionRoleAccess } from '@/common/composables/useActionRoleAccess'
import MAction from '@/components/platform/MAction.vue'
import Button from 'primevue/button'
import Menu from 'primevue/menu'
import { computed, ref, inject, onMounted } from 'vue'
import { getPrimeIcon } from '@/common/utils/iconMapper'
import { useDropdownGroupActionRegistry } from '@/common/composables/useDropdownGroupActionRegistry'

interface Props {
  config: any
  position: string[]
  level?: string
  targetElementId?: string
}

const props = withDefaults(defineProps<Props>(), {
  level: 'element',
})

onMounted(async () => {
})

const { pageActions, pageRegistryFormId } = usePageData()
const { hasActionRoleAccess } = useActionRoleAccess()

const getDropDownName = (action: any): string => {
  const dropDown = action.dropDownActionName
  if (Array.isArray(dropDown)) {
    return dropDown[0]?._text?.trim() || ''
  }
  return dropDown?._text?.trim() || ''
}

const isDropDownMain = (action: any): boolean => {
  const dropDown = action.dropDownMain
  const value = Array.isArray(dropDown) ? dropDown[0]?._text : dropDown?._text
  return value === 'true' || value === true
}

const hasRequiredRole = (action: any): boolean => hasActionRoleAccess(action.roles)

// Computed properties
const panelActions = computed(() => {
  if (!pageActions.value) return []

  if (props.level === 'panel') {
    const filtered = pageActions.value.filter((action) => {
      const actionLevel = action.layout?.level?._text
      const actionId = action.layout?.id?._text
      const actionPosition = action.layout?.position?._text

      const levelMatch = actionLevel === 'panel'
      const idMatch = actionId === props.config.panelId
      const positionMatch = typeof actionPosition === 'string' && props.position.includes(actionPosition)
      const roleMatch = hasRequiredRole(action)

      // CRITICAL: Exclude ALL actions with dropDownMain="false" that have a dropDownActionName
      // These should NEVER render as standalone buttons - only in dropdowns
      const dropDownName = getDropDownName(action)
      const hasDropDownGroup = dropDownName !== ''

      if (hasDropDownGroup && !isDropDownMain(action)) {
        // This action should only appear in a dropdown menu, never standalone
        return false
      }

      return levelMatch && idMatch && positionMatch && roleMatch
    })
    return filtered
  }

  const filtered = pageActions.value.filter((action) => {
    const actionLevel = action.layout?.level?._text
    const actionId = action.layout?.id?._text
    const actionPosition = action.layout?.position?._text

    const levelMatch = actionLevel === 'element'
    const idMatch = props.targetElementId
      ? actionId === props.targetElementId
      : actionId === props.config.panelId
    const positionMatch = typeof actionPosition === 'string' && props.position.includes(actionPosition)
    const roleMatch = hasRequiredRole(action)

    // CRITICAL: Exclude ALL actions with dropDownMain="false" that have a dropDownActionName
    // These should NEVER render as standalone buttons - only in dropdowns
    const dropDownName = getDropDownName(action)
    const hasDropDownGroup = dropDownName !== ''

    if (hasDropDownGroup && !isDropDownMain(action)) {
      // This action should only appear in a dropdown menu, never standalone
      return false
    }

    return levelMatch && idMatch && positionMatch && roleMatch
  })
  return filtered
})

// Group actions by dropDownActionName, collecting from ALL positions globally
const groupedActions = computed(() => {
  const sorted = [...panelActions.value].sort((a, b) => {
    const sortA = parseInt(a._attributes?.sort) || 0
    const sortB = parseInt(b._attributes?.sort) || 0
    return sortA - sortB
  })

  // Build global groups map from ALL page actions for dropdown groups
  const globalGroups: Map<string, any[]> = new Map()

  pageActions.value.forEach((action) => {
    const dropDownName = action.dropDownActionName?._text
    if (dropDownName && dropDownName.trim() !== '') {
      if (!globalGroups.has(dropDownName)) {
        globalGroups.set(dropDownName, [])
      }
      globalGroups.get(dropDownName)!.push(action)
    }
  })

  // Now process only the filtered actions for this position
  const groups: Map<string, any[]> = new Map()
  const ungrouped: any[] = []

  sorted.forEach((action) => {
    const dropDownName = action.dropDownActionName?._text
    const hasDropDownGroup = dropDownName && dropDownName.trim() !== ''

    if (hasDropDownGroup) {
      // Use the global group data
      if (globalGroups.has(dropDownName)) {
        if (!groups.has(dropDownName)) {
          groups.set(dropDownName, globalGroups.get(dropDownName)!)
        }
      }
    } else {
      ungrouped.push(action)
    }
  })

  // Create final list
  const result: any[] = []

  // Add ungrouped actions
  ungrouped.forEach((action) => {
    result.push({ type: 'single', action })
  })

  // Add grouped actions (only once per group)
  const processedGroups = new Set<string>()

  sorted.forEach((action) => {
    const dropDownName = action.dropDownActionName?._text
    if (dropDownName && dropDownName.trim() !== '' && !processedGroups.has(dropDownName)) {
      processedGroups.add(dropDownName)

      const groupActions = globalGroups.get(dropDownName) || []
      const mainAction = groupActions.find((a) => a.dropDownMain?._text === 'true')

      // Only render the group if the main action is in the current filtered actions
      if (sorted.includes(mainAction) || (!mainAction && sorted.includes(groupActions[0]))) {
        result.push({
          type: 'group',
          groupName: dropDownName,
          mainAction: mainAction || groupActions[0],
          actions: groupActions, // All actions from all positions
        })
      }
    }
  })

  return result
})

const sortedPanelActions = computed(() => groupedActions.value)

const hasActions = computed(() => sortedPanelActions.value.length > 0)

const positionClasses = computed(() => {
  const classes: Record<string, boolean> = {}
  props.position.forEach((pos) => {
    classes[`position-${pos}`] = true
  })
  return classes
})

// Menu refs management
const menuRefs = ref<Map<number, any>>(new Map())

const setMenuRef = (el: any, index: number) => {
  if (el) {
    menuRefs.value.set(index, el)
  }
}

const toggleDropdown = (event: Event, index: number) => {
  const menu = menuRefs.value.get(index)
  if (menu) {
    menu.toggle(event)
  }
}

// Get onAction from injection
const onAction = inject<(context: any) => void>('onAction', () => {})

const executeDropdownAction = async (action: any) => {
  const ctx = {
    id: action._attributes ? +action._attributes.id : 0,
    name: action.name?._text || '',
    elementId: props.targetElementId,
    level: props.level,
    data: action,
  }

  await onAction({ getContext: () => ctx })
}

const registryFormId = computed(() => {
  if (pageRegistryFormId.value) {
    const n = parseInt(pageRegistryFormId.value, 10)
    if (Number.isFinite(n)) return n
  }
  return 0
})

const targetElementId = computed(() => props.targetElementId || props.config?.panelId || '')

const { isDropdownMenuActionVisible, isDropdownMenuActionEnabled, dropdownActionLabel } = useDropdownGroupActionRegistry({
  groupedActions,
  formId: registryFormId,
  elementId: targetElementId,
  onActivateDropdown: executeDropdownAction,
})

// Format actions for PrimeVue Menu
const getDropdownItems = (actions: any[]) => {
  return actions
    .filter((action) => isDropdownMenuActionVisible(action))
    .map((action) => ({
      label: dropdownActionLabel(action),
      icon: getPrimeIcon(action.image?._text || ''),
      disabled: !isDropdownMenuActionEnabled(action),
      command: () => executeDropdownAction(action),
    }))
}
</script>

<style scoped lang="scss">
.panel-actions {
  display: flex;
  gap: 0.5rem;
  padding: 0.5rem 0;

  &.position-bottom {
    margin-top: auto;
  }

  &.position-top {
    margin-bottom: 0.5rem;
    justify-content: flex-end;
  }

  &.position-right {
    justify-content: flex-end;
    margin-left: auto;
  }

  &.position-left {
    justify-content: flex-start;
  }

  // Fix text wrapping for all action buttons
  :deep(.m-button .m-label__text) {
    white-space: nowrap !important;
  }
}

.panel-action-item {
  flex-shrink: 0;
}

.action-group {
  display: inline-flex;
  align-items: stretch;
  gap: 0;
  height: 2.5rem;

  .main-action {
    :deep(.p-button) {
      border-top-right-radius: 0 !important;
      border-bottom-right-radius: 0 !important;
      border-right: none !important;
      margin: 0 !important;
      height: 100%;
    }
  }
}

// Dropdown toggle button - very specific selectors to override PrimeVue
.action-dropdown-toggle {
  border-radius: 0 !important;
  border-top-right-radius: 4px !important;
  border-bottom-right-radius: 4px !important;
  border: 1px solid var(--primary-color) !important;
  border-left: 1px solid rgba(255, 255, 255, 0.3) !important;
  min-width: auto !important;
  width: 2rem !important;
  height: 100% !important;
  padding: 0 !important;
}

.action-dropdown-toggle,
.action-dropdown-toggle.p-button,
.action-dropdown-toggle.p-button.p-button-sm {
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
  color: white !important;

  .p-button-icon {
    font-size: 0.75rem !important;
    color: white !important;
  }

  .p-button-label {
    display: none;
  }
}

.action-dropdown-toggle:hover,
.action-dropdown-toggle.p-button:hover,
.action-dropdown-toggle.p-button.p-button-sm:hover,
.action-dropdown-toggle:enabled:hover,
.action-dropdown-toggle.p-button:enabled:hover,
.action-dropdown-toggle:not(:disabled):hover,
.action-dropdown-toggle.p-button:not(:disabled):hover {
  background: var(--primary-600) !important;
  background-color: var(--primary-600) !important;
  color: white !important;
  border-color: var(--primary-600) !important;

  .p-button-icon {
    color: white !important;
  }
}

.action-dropdown-toggle:focus,
.action-dropdown-toggle.p-button:focus {
  box-shadow: none !important;
  background: var(--primary-color) !important;
  background-color: var(--primary-color) !important;
}
</style>
